import { useEffect, useRef } from 'react';
import { useLandmarkStore } from '../state/landmarks';
import type {
  HeritageContext,
  HeritageNeighbor,
  HeritagePhoto,
  ResearchLead,
  TimelineEntry,
} from '../types/business';

export function BusinessSidePanel() {
  const business = useLandmarkStore((state) => state.selectedBusiness);
  const clearSelection = useLandmarkStore((state) => state.clearSelection);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (business !== null) closeButtonRef.current?.focus();
  }, [business]);

  useEffect(() => {
    if (business === null) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clearSelection();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [business, clearSelection]);

  if (business === null) return null;

  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-labelledby="business-panel-title"
      data-testid="business-side-panel"
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(420px, 100vw)',
        background: 'var(--surface-raised)',
        boxShadow: 'var(--shadow-popover)',
        borderLeft: `4px solid var(--accent)`,
        padding: 'var(--space-6)',
        overflowY: 'auto',
        zIndex: 10,
      }}
    >
      <button
        ref={closeButtonRef}
        type="button"
        onClick={clearSelection}
        aria-label="Close business details"
        data-testid="business-panel-close"
        style={{
          position: 'absolute',
          top: 'var(--space-3)',
          right: 'var(--space-3)',
          width: 32,
          height: 32,
          borderRadius: 'var(--radius-sm)',
          color: 'var(--text-muted)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg
          width={18}
          height={18}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M6 6L18 18M18 6L6 18" />
        </svg>
      </button>

      <p
        style={{
          marginTop: 'var(--space-4)',
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          fontWeight: 600,
        }}
      >
        Featured · {business.category}
      </p>
      <h2
        id="business-panel-title"
        style={{
          marginTop: 'var(--space-1)',
          fontSize: '1.5rem',
          fontWeight: 600,
        }}
      >
        {business.name}
      </h2>
      {business.tagline ? (
        <p
          style={{
            marginTop: 'var(--space-1)',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
            fontFamily: 'var(--font-serif)',
          }}
        >
          {business.tagline}
        </p>
      ) : null}

      <p style={{ marginTop: 'var(--space-3)', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
        {business.address}
      </p>

      <p
        style={{
          marginTop: 'var(--space-4)',
          fontFamily: 'var(--font-serif)',
          fontSize: '1rem',
          lineHeight: 1.55,
        }}
      >
        {business.description}
      </p>

      {business.links.length > 0 ? (
        <div style={{ marginTop: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {business.links.map((link) => (
            <a
              key={link.url}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`business-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
              style={{
                display: 'inline-block',
                padding: '10px var(--space-4)',
                background: 'var(--primary)',
                color: '#fffdf2',
                borderRadius: 'var(--radius-sm)',
                textAlign: 'center',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              {link.label} →
            </a>
          ))}
        </div>
      ) : null}

      {business.heritage ? <HeritageSection heritage={business.heritage} /> : null}
    </aside>
  );
}

function HeritageSection({ heritage }: { heritage: HeritageContext }) {
  const statusLabel: Record<HeritageContext['buildingStatus'], string> = {
    no_individual_record: 'No individual heritage record',
    inventoried: 'In the heritage inventory',
    protected_monument: 'Protected monument',
  };
  return (
    <section
      data-testid="business-heritage"
      style={{
        marginTop: 'var(--space-8)',
        paddingTop: 'var(--space-6)',
        borderTop: '1px solid var(--border)',
      }}
    >
      <p
        style={{
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          fontWeight: 600,
        }}
      >
        Heritage context
      </p>
      <p
        style={{
          marginTop: 'var(--space-1)',
          fontSize: '0.875rem',
          color: 'var(--text-muted)',
        }}
      >
        {statusLabel[heritage.buildingStatus]}
      </p>
      <p
        style={{
          marginTop: 'var(--space-3)',
          fontFamily: 'var(--font-serif)',
          fontSize: '0.95rem',
          lineHeight: 1.6,
        }}
      >
        {heritage.summary}
      </p>

      {heritage.buildingPhotos && heritage.buildingPhotos.length > 0 ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600 }}>The building itself</h3>
          <p style={{ marginTop: 'var(--space-1)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Archival photographs in which #81 appears under its own number, or in which it is captured
            in the frame edge of a neighbour's elevation shot.
          </p>
          <div style={{ marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {heritage.buildingPhotos.map((p, i) => (
              <BlockPhoto key={`bp-${p.url}-${i}`} photo={p} highlight />
            ))}
          </div>
        </div>
      ) : null}

      {heritage.timeline && heritage.timeline.length > 0 ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600 }}>Use over time</h3>
          <ol
            style={{
              marginTop: 'var(--space-3)',
              listStyle: 'none',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
              borderLeft: '2px solid var(--border)',
              paddingLeft: 'var(--space-4)',
            }}
          >
            {heritage.timeline.map((t, i) => (
              <TimelineRow key={`t-${i}`} entry={t} />
            ))}
          </ol>
        </div>
      ) : null}

      {heritage.nearby.length > 0 ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600 }}>Nearby heritage</h3>
          <ul
            style={{
              marginTop: 'var(--space-3)',
              listStyle: 'none',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
            }}
          >
            {heritage.nearby.map((n) => (
              <NeighborCard key={n.oerId} neighbor={n} />
            ))}
          </ul>
        </div>
      ) : null}

      {heritage.photos && heritage.photos.length > 0 ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600 }}>Block photos</h3>
          <div
            style={{
              marginTop: 'var(--space-3)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-4)',
            }}
          >
            {heritage.photos.map((p, i) => (
              <BlockPhoto key={`${p.url}-${i}`} photo={p} />
            ))}
          </div>
        </div>
      ) : null}

      {heritage.researchLeads && heritage.researchLeads.length > 0 ? (
        <div style={{ marginTop: 'var(--space-6)' }}>
          <h3 style={{ fontSize: '1.05rem', fontWeight: 600 }}>Research leads</h3>
          <p style={{ marginTop: 'var(--space-1)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Maps, paintings, postcards, archives, books, films, and direct photo IDs to chase further.
          </p>
          <ul
            style={{
              marginTop: 'var(--space-3)',
              listStyle: 'none',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
            }}
          >
            {heritage.researchLeads.map((lead, i) => (
              <ResearchLeadCard key={`${lead.url}-${i}`} lead={lead} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function ResearchLeadCard({ lead }: { lead: ResearchLead }) {
  return (
    <li
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)', justifyContent: 'space-between' }}>
        <a
          href={lead.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: '0.95rem', fontWeight: 600, fontFamily: 'var(--font-serif)', flex: 1 }}
        >
          {lead.label} →
        </a>
        {lead.category ? <CategoryTag category={lead.category} /> : null}
      </div>
      {lead.image ? (
        <a
          href={lead.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            marginTop: 'var(--space-2)',
            borderRadius: 'var(--radius-sm)',
            overflow: 'hidden',
            border: '1px solid var(--border)',
          }}
        >
          <img
            src={lead.image.src}
            alt={lead.image.alt}
            loading="lazy"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </a>
      ) : null}
      <p style={{ marginTop: 'var(--space-2)', fontSize: '0.85rem', lineHeight: 1.5 }}>
        {lead.note}
      </p>
    </li>
  );
}

const CATEGORY_STYLES: Record<NonNullable<ResearchLead['category']>, { bg: string; fg: string; label: string }> = {
  archive: { bg: 'var(--primary)', fg: '#fffdf2', label: 'Archive' },
  painting: { bg: 'var(--secondary)', fg: '#fffdf2', label: 'Painting' },
  map: { bg: 'var(--accent)', fg: '#1a1410', label: 'Map' },
  postcard: { bg: 'var(--info)', fg: '#fffdf2', label: 'Postcard' },
  video: { bg: 'var(--error)', fg: '#fffdf2', label: 'Video' },
  book: { bg: 'var(--warning)', fg: '#1a1410', label: 'Book' },
  photo: { bg: 'var(--success)', fg: '#fffdf2', label: 'Photo' },
};

function CategoryTag({ category }: { category: NonNullable<ResearchLead['category']> }) {
  const s = CATEGORY_STYLES[category];
  return (
    <span
      style={{
        fontSize: '0.6rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 700,
        padding: '2px 6px',
        background: s.bg,
        color: s.fg,
        borderRadius: 'var(--radius-sm)',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      {s.label}
    </span>
  );
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  return (
    <li
      style={{
        position: 'relative',
        paddingLeft: 'var(--space-2)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: -22,
          top: 6,
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: 'var(--accent)',
          border: '2px solid var(--surface-raised)',
        }}
      />
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
        {entry.date}
      </div>
      <div style={{ fontWeight: 600, fontSize: '0.95rem', fontFamily: 'var(--font-serif)' }}>
        {entry.label}
      </div>
      {entry.detail ? (
        <p style={{ marginTop: 4, fontSize: '0.85rem', lineHeight: 1.5 }}>
          {entry.detail}
          {entry.sourceUrl ? (
            <>
              {' '}
              <a href={entry.sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.75rem' }}>
                source ↗
              </a>
            </>
          ) : null}
        </p>
      ) : null}
    </li>
  );
}

function NeighborCard({ neighbor }: { neighbor: HeritageNeighbor }) {
  return (
    <li
      data-testid={`heritage-neighbor-${neighbor.oerId}`}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      {neighbor.photoUrl ? (
        <a
          href={`https://inventaris.onroerenderfgoed.be/erfgoedobjecten/${neighbor.oerId}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open Onroerend Erfgoed record for ${neighbor.name}`}
        >
          <img
            src={neighbor.photoUrl}
            alt={`${neighbor.name}, ${neighbor.address}`}
            loading="lazy"
            style={{
              width: '100%',
              height: 160,
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </a>
      ) : null}
      <div style={{ padding: 'var(--space-3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-2)' }}>
          <h4 style={{ fontSize: '0.95rem', fontWeight: 600, fontFamily: 'var(--font-serif)' }}>
            {neighbor.name}
          </h4>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
            ~{neighbor.distanceM} m
          </span>
        </div>
        <p style={{ marginTop: 'var(--space-1)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          {neighbor.address} · {neighbor.period}
          {neighbor.isProtected ? (
            <span
              style={{
                marginLeft: 'var(--space-2)',
                padding: '2px 6px',
                background: 'var(--accent)',
                color: 'var(--text-headline)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Protected
            </span>
          ) : null}
        </p>
        <p style={{ marginTop: 'var(--space-2)', fontSize: '0.85rem', lineHeight: 1.5 }}>
          {neighbor.description}
        </p>
        <div style={{ marginTop: 'var(--space-3)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <a
            href={`https://inventaris.onroerenderfgoed.be/erfgoedobjecten/${neighbor.oerId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: '0.8rem' }}
          >
            Heritage record →
          </a>
          {neighbor.photoUrl && neighbor.photoAttribution ? (
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              Photo: {neighbor.photoAttribution}
              {neighbor.photoLicense ? (
                <>
                  {' · '}
                  {neighbor.photoLicenseUrl ? (
                    <a href={neighbor.photoLicenseUrl} target="_blank" rel="noopener noreferrer">
                      {neighbor.photoLicense}
                    </a>
                  ) : (
                    neighbor.photoLicense
                  )}
                </>
              ) : null}
            </span>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function BlockPhoto({ photo, highlight = false }: { photo: HeritagePhoto; highlight?: boolean }) {
  return (
    <figure
      style={{
        margin: 0,
        background: 'var(--surface)',
        border: highlight ? '2px solid var(--accent)' : '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <a href={photo.sourceUrl} target="_blank" rel="noopener noreferrer">
        <img
          src={photo.url}
          alt={photo.caption}
          loading="lazy"
          style={{
            width: '100%',
            height: 200,
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </a>
      <figcaption style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '0.8rem', lineHeight: 1.4 }}>
        {photo.caption}
        <br />
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
          <a href={photo.sourceUrl} target="_blank" rel="noopener noreferrer">
            {photo.source}
          </a>
          {' · '}
          <a href={photo.licenseUrl} target="_blank" rel="noopener noreferrer">
            {photo.license}
          </a>
        </span>
      </figcaption>
    </figure>
  );
}
